import React, {
  useEffect,
  useState,
} from 'react'
import {
  Button,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap'
import {
  EyeIcon,
  EyeOffIcon,
} from 'lucide-react'
import { useCurrentUserQuery, useChangePasswordMutation } from '@data/currentUser/currentUser'
import { useLogout } from '@hooks/useLogout'

import './ChangePassword.scss'
import { validatePasswordStructure } from '@common/utils/validation/authValidators'

interface IChangePasswordModalProps {
  show: boolean
  onHide: () => void
}

const ChangePassword: React.FC<IChangePasswordModalProps> = ({
                                                               show,
                                                               onHide,
                                                             }: IChangePasswordModalProps) => {
  const { data: userInfo } = useCurrentUserQuery()
  const changePasswordMutation = useChangePasswordMutation()
  const logout = useLogout()

  const [currentPassword, setCurrentPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [verifyPassword, setVerifyPassword] = useState<string>('')
  const [isPasswordShown, setIsPasswordShown] = useState<boolean>(false)

  const [isNewPasswordValid, setIsNewPasswordValid] = useState<boolean>(false)
  const [doNewPasswordsMatch, setDoNewPasswordsMatch] = useState<boolean>(false)

  const [passwordUpdated, setPasswordUpdated] = useState<boolean>(false)

  useEffect(() => {
    if (changePasswordMutation.isSuccess) {
      setCurrentPassword('')
      setNewPassword('')
      setVerifyPassword('')
      setPasswordUpdated(true)
      setTimeout(() => {
        logout('password-change')
      }, 5000)
    }
  }, [changePasswordMutation.isSuccess])

  useEffect(() => {
    if (!show) {
      setPasswordUpdated(false)
      changePasswordMutation.reset()
    }
  }, [show])

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    if (
      currentPassword &&
      newPassword &&
      verifyPassword &&
      isNewPasswordValid &&
      doNewPasswordsMatch
    ) {
      changePasswordMutation.mutate({ currentPassword, newPassword, verifyPassword })
    }
  }

  const onCurrentPasswordChange = (password: string) => {
    setCurrentPassword(password)
  }

  const onNewPasswordChange = (password: string) => {
    setNewPassword(password)
    const isValid = validatePasswordStructure(password)
    setIsNewPasswordValid(isValid)
  }

  const onNewConfirmationPasswordChange = (password: string) => {
    setVerifyPassword(password)
    const areMatching = newPassword === password
    setDoNewPasswordsMatch(areMatching)
  }

  return (
    <Modal
      onHide={onHide}
      show={show}
      backdrop='static'
      size='lg'
      aria-labelledby='contained-modal-title-vcenter'
      centered
    >
      <Modal.Header
        className=''
        closeButton
      >
        <Modal.Title id='contained-modal-title-vcenter'>
          Reset Password
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='custom-modal-body'>
        <Form onSubmit={handleSubmit}>

          {/* HIDDEN USERNAME FIELD FOR ACCESSIBILITY / PASSWORD MANAGERS */}
          <Form.Control
            type='text'
            autoComplete='username'
            value={userInfo?.email || ''}
            readOnly
            hidden
          />

          {/* CURRENT PASSWORD GROUP */}
          <Form.Group
            as={Row}
            controlId='update-password-1-id'
            className='mb-3'
          >
            <Form.Label column sm={12}>
              Current Password
            </Form.Label>
            <Col>
              <InputGroup>
                <Form.Control
                  type={isPasswordShown ? 'text' : 'password'}
                  placeholder='Enter your current password'
                  autoComplete='current-password'
                  onChange={(e) => onCurrentPasswordChange(e.currentTarget.value)}
                  isInvalid={!currentPassword && !!newPassword}
                />
                <InputGroup.Text
                  role='button'
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsPasswordShown(!isPasswordShown)}
                >
                  {isPasswordShown ? <EyeIcon /> : <EyeOffIcon />}
                </InputGroup.Text>
              </InputGroup>
            </Col>
          </Form.Group>

          {/* NEW PASSWORD GROUP */}
          <Form.Group
            as={Row}
            controlId='update-password-2-id'
            className='mb-3'
          >
            <Form.Label column sm={12}>
              New Password
            </Form.Label>
            <Col>
              <InputGroup hasValidation>
                <Form.Control
                  type={isPasswordShown ? 'text' : 'password'}
                  placeholder='Enter new password'
                  autoComplete='new-password'
                  onChange={(e) => onNewPasswordChange(e.currentTarget.value)}
                  isInvalid={!!newPassword && !isNewPasswordValid}
                />
                <InputGroup.Text
                  role='button'
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsPasswordShown(!isPasswordShown)}
                >
                  {isPasswordShown ? <EyeIcon /> : <EyeOffIcon />}
                </InputGroup.Text>

                <Form.Control.Feedback type='invalid'>
                  <div className='input-feedback_error'>
                    Password should be:
                    <ul className='mb-0'>
                      <li>between 8 and 30 characters long </li>
                      <li>have a minimum of 1 letter </li>
                      <li>have a minimum of 1 uppercase letter </li>
                      <li>have a minimum of 1 numeric character </li>
                      <li>have a minimum of 1 special character {" (!\"#$%&'()*+,-./:;<=>?@£¬[]^_`{|}~)"}</li>
                    </ul>
                  </div>
                </Form.Control.Feedback>
              </InputGroup>
            </Col>
          </Form.Group>

          {/* CONFIRM PASSWORD GROUP */}
          <Form.Group
            as={Row}
            controlId='update-password-3-id'
            className='mb-3'
          >
            <Form.Label column sm={12}>
              Confirm New Password
            </Form.Label>
            <Col>
              <InputGroup hasValidation>
                <Form.Control
                  type={isPasswordShown ? 'text' : 'password'}
                  placeholder='Enter new password again'
                  autoComplete='new-password'
                  onChange={(e) => onNewConfirmationPasswordChange(e.currentTarget.value)}
                  isInvalid={!!newPassword && !!verifyPassword && !doNewPasswordsMatch}
                />
                <InputGroup.Text
                  role='button'
                  style={{ cursor: 'pointer' }}
                  onClick={() => setIsPasswordShown(!isPasswordShown)}
                >
                  {isPasswordShown ? <EyeIcon /> : <EyeOffIcon />}
                </InputGroup.Text>
                <Form.Control.Feedback type='invalid'>
                  Passwords don't match!
                </Form.Control.Feedback>
              </InputGroup>
            </Col>
          </Form.Group>

          {/* SUBMIT BUTTON */}
          <Button
            variant='primary'
            type='submit'
            disabled={
              !currentPassword ||
              !newPassword ||
              !verifyPassword ||
              !isNewPasswordValid ||
              !doNewPasswordsMatch ||
              passwordUpdated
            }
          >
            {changePasswordMutation.isPending ? (
              <>
                <Spinner
                  as='span'
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                  className='me-2'
                />
                <span>Updating password...</span>
              </>
            ) : (
              'Update Password'
            )}
          </Button>
        </Form>
      </Modal.Body>
      <Modal.Footer className='custom-modal-footer'>
        <div>
          <strong>Note: </strong> You will be logged out after changing your password
        </div>
        <Button
          id='close'
          variant='secondary'
          disabled={changePasswordMutation.isPending || passwordUpdated}
          onClick={onHide}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ChangePassword
